import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense";
import useFastmagIntegrationUpdateMutation from "@babackapp/private-api-client/client/use-update-fastmag-integration";
import { Button } from "@babackapp/ui/components/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from "@babackapp/ui/components/form";
import { Input } from "@babackapp/ui/components/input";
import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback } from "react";
import { useForm } from "react-hook-form";
import useTranslation from "@babackapp/translations/use-translation";
import { z } from "zod";
import IntegrationPreview from "./integration-preview";

const formSchema = z.object({
  activated: z.boolean(),
  server: z.string(),
  brandCode: z.string(),
  store: z.string(),
  account: z.string(),
  password: z.string(),
});

const FastmagIntegrationForm = () => {
  const t = useTranslation();

  const { data: shop } = useCurrentShopSuspense();

  const { activated, server, brandCode, store, account, password } =
    shop.integrations.fastmag;

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: { activated, server, brandCode, store, account, password },
  });

  const { mutateAsync: updateFastmagIntegration } =
    useFastmagIntegrationUpdateMutation(shop);

  const onSubmit = form.handleSubmit(async (data) => {
    await updateFastmagIntegration(data);
    return form.reset(data);
  });

  return (
    <Form {...form}>
      <form onSubmit={onSubmit} className="flex flex-col justify-start gap-4">
        <FormField
          control={form.control}
          name="server"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                {t("settings.integrations.thirdpart.fastmag.server")}
              </FormLabel>
              <FormControl>
                <Input {...field} />
              </FormControl>
            </FormItem>
          )}
        />

        <div className="flex items-center gap-4">
          <FormField
            control={form.control}
            name="brandCode"
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormLabel>
                  {t("settings.integrations.thirdpart.fastmag.brandCode")}
                </FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="store"
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormLabel>
                  {t("settings.integrations.thirdpart.fastmag.shopName")}
                </FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
              </FormItem>
            )}
          />
        </div>

        <div className="flex items-center gap-4">
          <FormField
            control={form.control}
            name="account"
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormLabel>
                  {t("settings.integrations.thirdpart.fastmag.account")}
                </FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormLabel>
                  {t("settings.integrations.thirdpart.fastmag.password")}
                </FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
              </FormItem>
            )}
          />
        </div>

        <Button type="submit" disabled={!form.formState.isDirty}>
          {t("misc.save")}
        </Button>
      </form>
    </Form>
  );
};

export function FastmagIntegrationPreview() {
  const t = useTranslation();
  const { data: shop } = useCurrentShopSuspense();

  const { mutateAsync: updateActivation } =
    useFastmagIntegrationUpdateMutation(shop);

  const onActivationUpdate = useCallback(
    (activated: boolean) => {
      if (!shop) return;
      updateActivation({ activated });
    },
    [shop, updateActivation],
  );

  if (!shop || !shop.plan.fastmag) return null;

  return (
    <IntegrationPreview
      title="Fastmag"
      bgColor="#E8005F"
      logo="/integrations/fastmag.png"
      textColor="black"
      description={t("settings.integrations.thirdpart.fastmag.description")}
      onActivationUpdate={onActivationUpdate}
      activated={shop.integrations.fastmag.activated}
      seeMoreUrl="https://fastmag.fr/"
    >
      <FastmagIntegrationForm />
    </IntegrationPreview>
  );
}
